import {
  POS_SALE_KIND_LABELS,
  POS_SALE_KINDS,
  POS_SALE_STATUS_LABELS,
  POS_SALE_STATUSES,
  type PosReportRow,
  type PosSaleSummary,
  posSaleKindSchema,
  posSaleStatusSchema,
} from "@station/contracts";
import { addDays, type LocalDate, Money, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Receipt } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChartWithTable } from "../../../../features/fuel/shared.js";
import { posReportChartOption, tenderTotalsText } from "../../../../features/pos/pos-values.js";
import {
  type PosSaleFilters,
  posSalesQuery,
  usePosReport,
  usePosSales,
  usePosStaffOptions,
} from "../../../../features/pos/queries.js";
import {
  NoStations,
  PosPage,
  PosSaleStatusBadge,
  PosStationField,
  posSearchShape,
  PosWarningBadge,
  TenderBadges,
  tenderLabel,
  usePosStations,
} from "../../../../features/pos/shared.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

const SALE_TABS = ["sales", "summary"] as const;
type SaleTab = (typeof SALE_TABS)[number];

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...posSearchShape,
  tab: z.enum(SALE_TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  staff: z.string().uuid().optional().catch(undefined),
  shift: z.string().uuid().optional().catch(undefined),
  status: posSaleStatusSchema.optional().catch(undefined),
  kind: posSaleKindSchema.optional().catch(undefined),
  flagged: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kasa/satislar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PosSalesRoute,
});

/**
 * Kasa satışları (Prompt 15 — web). İki sekme: fiş LİSTESİ (cursor sayfalama) ve gün × istasyon × kasiyer
 * ÖZETİ (grafik + tablo + Excel).
 *
 * Satışlar buradan GİRİLMEZ: kasa fişi yalnızca ajan senkronundan düşer. Ekran yalnızca okur; düzeltme
 * (iptal / iade / ödeme düzeltmesi) kasanın kendisinde müdür PIN'iyle yapılır.
 */
function PosSalesRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <PosPage
      title={t("menu.posSales")}
      description={t("pos.sales.description")}
      breadcrumb={[{ label: t("menu.posSales") }]}
    >
      {({ companyId }) => <PosSalesBody companyId={companyId} />}
    </PosPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function PosSalesBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = usePosStations(companyId);
  const tab: SaleTab = search.tab ?? "sales";
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <PosStationField selection={station} />
        <DateRangePicker
          label={t("pos.sales.businessDateRange")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      <Tabs className="grid gap-4" value={tab} onValueChange={(next) => setSearch({ tab: next })}>
        <TabsList aria-label={t("pos.sales.tabsLabel")}>
          {SALE_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`pos.sales.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* İçerik MUTLAKA `TabsContent` içinde: tetikleyicinin `aria-controls` hedefi var olmalı. */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "sales" ? (
            <SalesTab
              companyId={companyId}
              stationId={station.stationId}
              range={range}
              onFilterChange={setSearch}
            />
          ) : null}
          {tab === "summary" ? (
            <SummaryTab companyId={companyId} stationId={station.stationId} range={range} />
          ) : null}
        </TabsContent>
      </Tabs>
    </>
  );
}

// ── Fiş listesi ──────────────────────────────────────────────────────────────
function SalesTab({
  companyId,
  stationId,
  range,
  onFilterChange,
}: {
  companyId: string;
  stationId: string | null;
  range: { from: string; to: string };
  onFilterChange: (patch: Record<string, unknown>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const staff = usePosStaffOptions(companyId, stationId);

  const filters: PosSaleFilters = {
    stationId: stationId ?? undefined,
    staffId: search.staff,
    shiftId: search.shift,
    status: search.status,
    kind: search.kind,
    businessDateFrom: range.from,
    businessDateTo: range.to,
    flaggedOnly: search.flagged === "true" ? true : undefined,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = usePosSales(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<PosSaleSummary>[]>(
    () => [
      {
        id: "receipt",
        header: t("pos.sales.fields.receipt"),
        hideable: false,
        exportValue: (row) => receiptText(row),
        cell: (row) => (
          <Link
            to="/kasa/satislar/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            {/* Fiş no tek satırda kalır (ÖKC seri · Z · fiş): kırılınca satır 3–4 kat uzuyordu. */}
            <span className="font-mono whitespace-nowrap">{receiptText(row)}</span>
            <span className="text-xs text-muted-foreground">{formatDate(row.businessDate)}</span>
          </Link>
        ),
      },
      {
        id: "occurredAt",
        header: t("pos.sales.fields.occurredAt"),
        exportValue: (row) => row.occurredAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.occurredAt)}</span>,
      },
      {
        id: "station",
        header: t("pos.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName ?? "—",
      },
      {
        id: "staff",
        header: t("pos.sales.fields.staff"),
        exportValue: (row) => row.staffName,
        cell: (row) => row.staffName ?? "—",
      },
      {
        id: "kind",
        header: t("pos.sales.fields.kind"),
        exportValue: (row) => POS_SALE_KIND_LABELS[row.kind],
        cell: (row) => POS_SALE_KIND_LABELS[row.kind],
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => POS_SALE_STATUS_LABELS[row.status],
        cell: (row) => <PosSaleStatusBadge status={row.status} />,
      },
      {
        id: "lineCount",
        header: t("pos.sales.fields.lineCount"),
        align: "right",
        exportValue: (row) => row.lineCount,
        cell: (row) => <span className="tabular">{formatCount(row.lineCount)}</span>,
      },
      {
        id: "gross",
        header: t("pos.sales.fields.gross"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.gross).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.gross} />,
      },
      {
        id: "discountTotal",
        header: t("pos.sales.fields.discount"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.discountTotal).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.discountTotal} />,
      },
      {
        id: "net",
        header: t("pos.sales.fields.net"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.net).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.net} className="font-medium" />,
      },
      {
        id: "tenders",
        header: t("pos.sales.fields.tenders"),
        exportValue: (row) => row.tenderTypes.map(tenderLabel).join(", "),
        cell: (row) => <TenderBadges types={row.tenderTypes} />,
      },
      {
        id: "warnings",
        header: t("pos.sales.fields.warnings"),
        exportValue: (row) => row.warnings.length,
        // Renk TEK BAŞINA anlam taşımaz: rozet uyarı sayısını METİNLE yazar, tam liste ayrıntıdadır.
        cell: (row) =>
          row.warnings.length === 0 && !row.afterDayLock ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <PosWarningBadge warnings={row.warnings} />
          ),
      },
      {
        id: "shift",
        header: t("pos.sales.fields.shift"),
        defaultVisible: false,
        exportValue: (row) => row.shiftNo,
        cell: (row) =>
          row.shiftId === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <Link
              to="/vardiya/vardiyalar/$id"
              params={{ id: row.shiftId }}
              className="underline underline-offset-4"
            >
              {row.shiftNo ?? t("pos.sales.openShift")}
            </Link>
          ),
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("pos.sales.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Receipt />}
          title={t("pos.sales.emptyTitle")}
          description={t("pos.sales.emptyDescription")}
        />
      }
      filters={
        <>
          {staff.data && staff.data.length > 0 ? (
            <FormField label={t("pos.sales.fields.staff")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={search.staff ?? ""}
                  onChange={(event) => onFilterChange({ staff: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {staff.data!.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.staffCode} {option.fullName}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          ) : null}
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) => onFilterChange({ status: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {POS_SALE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {POS_SALE_STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("pos.sales.fields.kind")} className="w-40">
            {(control) => (
              <Select
                {...control}
                value={search.kind ?? ""}
                onChange={(event) => onFilterChange({ kind: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {POS_SALE_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {POS_SALE_KIND_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <Checkbox
              checked={search.flagged === "true"}
              onChange={(event) => onFilterChange({ flagged: event.target.checked ? "true" : undefined })}
            />
            {t("pos.sales.flaggedOnly")}
          </label>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
      onExport={async (visible) => {
        const rows = await fetchAllPages<PosSaleSummary>(
          async (cursor) =>
            (
              await api.GET("/pos/sales", {
                params: { query: posSalesQuery(companyId, filters, cursor, 200) as never },
              })
            ).data! as { items: PosSaleSummary[]; page: { nextCursor: string | null; hasMore: boolean } },
        );
        await exportRowsToExcel({
          fileName: "kasa-satislari",
          sheetName: t("menu.posSales"),
          columns: visible,
          rows,
        });
      }}
    />
  );
}

/** Fiş üçlüsü: ÖKC seri no · Z no / fiş no. Fişsiz satışta (ÖKC atlandı) ajanın satış kimliği yazılır. */
function receiptText(row: PosSaleSummary): string {
  if (row.receiptNo === null && row.fiscalDeviceSerial === null) return row.agentSaleId;
  const parts = [row.fiscalDeviceSerial, row.zNo === null ? null : `Z${row.zNo}`, row.receiptNo].filter(
    (part) => part !== null && part !== undefined,
  );
  return parts.join(" · ");
}

// ── Özet raporu ──────────────────────────────────────────────────────────────
function SummaryTab({
  companyId,
  stationId,
  range,
}: {
  companyId: string;
  stationId: string | null;
  range: { from: string; to: string };
}): ReactElement {
  const { t } = useTranslation();
  const report = usePosReport(companyId, { ...range, stationId: stationId ?? undefined });

  const columns = useMemo<DataTableColumn<PosReportRow>[]>(
    () => [
      {
        id: "businessDate",
        header: t("pos.report.fields.businessDate"),
        hideable: false,
        exportValue: (row) => row.businessDate,
        cell: (row) => <span className="tabular">{formatDate(row.businessDate)}</span>,
      },
      {
        id: "station",
        header: t("pos.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName ?? "—",
      },
      {
        id: "staff",
        header: t("pos.sales.fields.staff"),
        exportValue: (row) => row.staffName,
        cell: (row) => row.staffName ?? t("pos.report.noStaff"),
      },
      {
        id: "saleCount",
        header: t("pos.report.fields.saleCount"),
        align: "right",
        exportValue: (row) => row.saleCount,
        cell: (row) => <span className="tabular">{formatCount(row.saleCount)}</span>,
      },
      {
        id: "returnCount",
        header: t("pos.report.fields.returnCount"),
        align: "right",
        exportValue: (row) => row.returnCount,
        cell: (row) => <span className="tabular">{formatCount(row.returnCount)}</span>,
      },
      {
        id: "voidCount",
        header: t("pos.report.fields.voidCount"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.voidCount,
        cell: (row) => <span className="tabular">{formatCount(row.voidCount)}</span>,
      },
      {
        id: "gross",
        header: t("pos.sales.fields.gross"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.gross).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.gross} />,
      },
      {
        id: "discountTotal",
        header: t("pos.sales.fields.discount"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.discountTotal).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.discountTotal} />,
      },
      {
        id: "net",
        header: t("pos.sales.fields.net"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.net).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.net} className="font-medium" />,
      },
      {
        id: "returnNet",
        header: t("pos.report.fields.returnNet"),
        align: "right",
        exportValue: (row) => Money.fromMinor(row.returnNet).formatTr({ symbol: false }),
        cell: (row) => <MoneyText value={row.returnNet} colorize />,
      },
      {
        id: "tenders",
        header: t("pos.report.fields.tenders"),
        exportValue: (row) => tenderTotalsText(row.tenders, tenderLabel),
        cell: (row) => <span className="text-xs">{tenderTotalsText(row.tenders, tenderLabel)}</span>,
      },
    ],
    [t],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (report.isPending) return <SkeletonRows rows={6} />;

  const rows = report.data!.rows;
  const totals = report.data!.totals;

  return (
    <>
      <Card className="grid gap-4 p-4">
        <h2 className="text-base font-semibold">{t("pos.report.chartTitle")}</h2>
        <ChartWithTable
          ariaLabel={t("pos.report.chartAria")}
          option={posReportChartOption(rows, {
            net: t("pos.sales.fields.net"),
            discount: t("pos.sales.fields.discount"),
            amountAxis: t("pos.report.amountAxis"),
          })}
          tableLabel={t("pos.report.chartTableLabel")}
        >
          <SimpleTable
            caption={t("pos.report.chartTableLabel")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("pos.report.fields.businessDate")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("pos.sales.fields.net")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("pos.sales.fields.discount")}
                </th>
              </tr>
            }
          >
            {dailyTotals(rows).map((day) => (
              <tr key={day.businessDate} className="border-t">
                <td className={tdClass}>{formatDate(day.businessDate)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={day.net} />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={day.discount} />
                </td>
              </tr>
            ))}
          </SimpleTable>
        </ChartWithTable>
      </Card>

      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("pos.report.totalsTitle")}</h2>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Total label={t("pos.report.fields.saleCount")} value={formatCount(totals.saleCount)} />
          <Total label={t("pos.report.fields.returnCount")} value={formatCount(totals.returnCount)} />
          <Total label={t("pos.sales.fields.net")}>
            <MoneyText value={totals.net} />
          </Total>
          <Total label={t("pos.sales.fields.discount")}>
            <MoneyText value={totals.discountTotal} />
          </Total>
        </dl>
        <p className="text-sm text-muted-foreground">{tenderTotalsText(totals.tenders, tenderLabel)}</p>
      </Card>

      <DataTable
        caption={t("pos.report.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.businessDate}:${row.stationId}:${row.staffId ?? "-"}`}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Receipt />}
            title={t("pos.report.emptyTitle")}
            description={t("pos.report.emptyDescription")}
          />
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: "kasa-ozeti",
            sheetName: t("pos.report.caption"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}

function Total({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: ReactElement;
}): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium tabular">{children ?? value}</dd>
    </div>
  );
}

/** Grafiğin TABLO ALTERNATİFİ için: satırlar güne toplanır (grafikteki seriyle birebir aynı sayılar). */
function dailyTotals(
  rows: readonly PosReportRow[],
): Array<{ businessDate: string; net: string; discount: string }> {
  const byDay = new Map<string, { net: bigint; discount: bigint }>();
  for (const row of rows) {
    const current = byDay.get(row.businessDate) ?? { net: 0n, discount: 0n };
    byDay.set(row.businessDate, {
      net: current.net + BigInt(row.net),
      discount: current.discount + BigInt(row.discountTotal),
    });
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([businessDate, totals]) => ({
      businessDate,
      net: totals.net.toString(),
      discount: totals.discount.toString(),
    }));
}
